"use client";

import { AnimatedWordCycler } from "@/components/about/animated-word-cycler";
import { OccasionalWiggle } from "@/components/about/occasional-wiggle";
import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";

const BACK_LEFT_SRC = "/images/marketing/home/hero/heroBackLeft.webp";
const BACK_RIGHT_SRC = "/images/marketing/home/hero/heroBackRight.webp";
const FRONT_LEFT_SRC = "/images/marketing/home/hero/heroFrontLeft.webp";
const FRONT_RIGHT_SRC = "/images/marketing/home/hero/heroFrontRight.webp";
const BACK_LAYER_MOBILE_SRC = "/images/marketing/home/hero/monsters-around-play-back-mobile.webp";
const FRONT_LAYER_MOBILE_SRC = "/images/marketing/home/hero/monsters-around-play-mobile.webp";

/** Overall cursor parallax travel in px at the section edges. */
const MAX_SHIFT_PX = 36;
/** Farther layer moves less (cursor). */
const BACK_PARALLAX_FACTOR = 0.35;
/** Closer monsters layer moves more (cursor). */
const FRONT_PARALLAX_FACTOR = 0.85;
/** Max upward travel (px) as the hero scrolls out of view. */
const MAX_SCROLL_SHIFT_PX = 220;
/** Farther layer rises less on scroll. */
const BACK_SCROLL_FACTOR = 0.55;
/** Closer layer rises more on scroll. */
const FRONT_SCROLL_FACTOR = 1;
/** Per-frame blend toward target parallax (0–1). Lower = smoother. */
const PARALLAX_SMOOTHING = 0.14;
/** Snap when close enough to avoid endless micro-updates. */
const PARALLAX_SNAP_PX = 0.08;

/**
 * Inner edge sits left/right of center by roughly half the CTA width, then
 * nudges in by a few px so the plates only kiss the middle content.
 */
const CONTENT_HALF = "9rem";
const BACK_OVERLAP = "0.4rem";
const FRONT_OVERLAP = "0.65rem";

function lerp(current: number, target: number, amount: number): number {
  const next = current + (target - current) * amount;
  return Math.abs(target - next) < PARALLAX_SNAP_PX ? target : next;
}

/** Vertically centered (`top: 50%`) parallax transform for side art. */
function sideParallaxStyle(xFactor: number, yFactor: number, scrollFactor: number): CSSProperties {
  return {
    transform: `translate3d(calc(var(--mx, 0px) * ${xFactor}), calc(-50% + var(--my, 0px) * ${yFactor} + var(--sy, 0px) * ${scrollFactor}), 0)`,
  };
}

function mobileParallaxStyle(
  xFactor: number,
  yFactor: number,
  scrollFactor: number
): CSSProperties {
  return {
    transform: `translate3d(calc(var(--mx, 0px) * ${xFactor}), calc(var(--my, 0px) * ${yFactor} + var(--sy, 0px) * ${scrollFactor}), 0)`,
  };
}

function HeroCta() {
  return (
    <div className="relative flex flex-col items-center text-center">
      <h1 className="text-7xl md:text-8xl drop-shadow-sm leading-tight">
        <span className="block leading-none">
          <AnimatedWordCycler
            className="block"
            words={[
              { text: "Mathy", color: "rgb(var(--accent-green))" },
              { text: "Thinky", color: "rgb(var(--accent-coral))" },
              { text: "Puzzly", color: "rgb(var(--accent-cyan))" },
            ]}
            transitionMs={450}
            holdMs={1000}
            staggerMs={40}
            outDistancePct={60}
          />
        </span>
        <span className="block text-white leading-none">Games!</span>
      </h1>
      <div className="mt-2 flex items-center justify-center gap-4">
        <OccasionalWiggle>
          <a
            href="/games"
            className="inline-block bg-orange-500 text-black px-10 md:px-14 py-5 md:py-7 rounded-full charlie-kingdom-font text-4xl md:text-5xl hover:bg-orange-400 shadow-cta-orange border-4 border-black relative rotate-[-3deg]"
          >
            <span className="relative -top-1">Play Math</span>
          </a>
        </OccasionalWiggle>
      </div>
      <p className="mt-4 text-lg md:text-2xl font-bold text-white">Free to play, no ads.</p>
    </div>
  );
}

function SideArtImage({
  src,
  side,
  depth,
}: {
  src: string;
  side: "left" | "right";
  depth: "back" | "front";
}) {
  const overlap = depth === "back" ? BACK_OVERLAP : FRONT_OVERLAP;
  const xFactor = depth === "back" ? BACK_PARALLAX_FACTOR : FRONT_PARALLAX_FACTOR;
  const yFactor = xFactor;
  const scrollFactor = depth === "back" ? BACK_SCROLL_FACTOR : FRONT_SCROLL_FACTOR;
  // Keep the plate on its side of center; only the inner edge crosses into the CTA a tad.
  const innerEdge =
    side === "left"
      ? { right: `calc(50% + ${CONTENT_HALF} - ${overlap})` }
      : { left: `calc(50% + ${CONTENT_HALF} - ${overlap})` };

  return (
    <img
      src={src}
      alt=""
      draggable={false}
      className={[
        "absolute top-1/2 h-full w-auto max-w-none object-contain object-center will-change-transform",
        depth === "front" ? "z-[1]" : "z-0",
      ].join(" ")}
      style={{
        ...innerEdge,
        ...sideParallaxStyle(xFactor, yFactor, scrollFactor),
      }}
    />
  );
}

function MobileArt({ children }: { children: ReactNode }) {
  return (
    <div className="relative mx-auto w-full max-w-[420px] md:hidden">
      <div className="relative w-full" aria-hidden="true">
        <img
          src={BACK_LAYER_MOBILE_SRC}
          alt=""
          draggable={false}
          className="block w-full h-auto max-w-none will-change-transform"
          style={mobileParallaxStyle(
            BACK_PARALLAX_FACTOR,
            BACK_PARALLAX_FACTOR,
            BACK_SCROLL_FACTOR
          )}
        />
        <img
          src={FRONT_LAYER_MOBILE_SRC}
          alt=""
          draggable={false}
          className="absolute left-0 top-0 block w-full h-auto max-w-none will-change-transform"
          style={mobileParallaxStyle(
            FRONT_PARALLAX_FACTOR,
            FRONT_PARALLAX_FACTOR,
            FRONT_SCROLL_FACTOR
          )}
        />
      </div>
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto">{children}</div>
      </div>
    </div>
  );
}

export function HeroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const targetMouseRef = useRef({ x: 0, y: 0 });
  const currentMouseRef = useRef({ x: 0, y: 0 });
  const targetScrollYRef = useRef(0);
  const currentScrollYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      el.style.setProperty("--mx", "0px");
      el.style.setProperty("--my", "0px");
      el.style.setProperty("--sy", "0px");
      return;
    }

    el.style.setProperty("--mx", "0px");
    el.style.setProperty("--my", "0px");
    el.style.setProperty("--sy", "0px");

    // Cache geometry so scroll handlers avoid getBoundingClientRect() (layout thrash).
    let heroPageLeft = 0;
    let heroPageTop = 0;
    let heroWidth = 0;
    let heroHeight = 0;

    const measureHero = () => {
      const rect = el.getBoundingClientRect();
      heroPageLeft = rect.left + window.scrollX;
      heroPageTop = rect.top + window.scrollY;
      heroWidth = rect.width;
      heroHeight = rect.height;
    };

    const tick = () => {
      const mouse = currentMouseRef.current;
      const mouseTarget = targetMouseRef.current;
      mouse.x = lerp(mouse.x, mouseTarget.x, PARALLAX_SMOOTHING);
      mouse.y = lerp(mouse.y, mouseTarget.y, PARALLAX_SMOOTHING);
      currentScrollYRef.current = lerp(
        currentScrollYRef.current,
        targetScrollYRef.current,
        PARALLAX_SMOOTHING
      );

      el.style.setProperty("--mx", `${mouse.x}px`);
      el.style.setProperty("--my", `${mouse.y}px`);
      el.style.setProperty("--sy", `${currentScrollYRef.current}px`);

      const settled =
        mouse.x === mouseTarget.x &&
        mouse.y === mouseTarget.y &&
        currentScrollYRef.current === targetScrollYRef.current;

      if (settled) {
        rafRef.current = null;
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const scheduleTick = () => {
      if (overlayFrozenRef.current) return;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    const updateScrollParallax = () => {
      // Remeasure only if mount saw empty geometry (common in jsdom / before layout).
      if (heroHeight <= 0) measureHero();
      if (heroHeight <= 0) {
        targetScrollYRef.current = 0;
      } else {
        const top = heroPageTop - window.scrollY;
        const progress = Math.min(1, Math.max(0, -top / heroHeight));
        targetScrollYRef.current = -progress * MAX_SCROLL_SHIFT_PX;
      }
      scheduleTick();
    };

    const onMove = (e: MouseEvent) => {
      if (heroWidth <= 0 || heroHeight <= 0) measureHero();
      if (heroWidth <= 0 || heroHeight <= 0) return;
      const left = heroPageLeft - window.scrollX;
      const top = heroPageTop - window.scrollY;
      const nx = (e.clientX - left) / heroWidth - 0.5;
      const ny = (e.clientY - top) / heroHeight - 0.5;
      targetMouseRef.current = { x: nx * MAX_SHIFT_PX, y: ny * MAX_SHIFT_PX };
      scheduleTick();
    };

    const onLeave = () => {
      targetMouseRef.current = { x: 0, y: 0 };
      scheduleTick();
    };

    const onResize = () => {
      measureHero();
      updateScrollParallax();
    };

    measureHero();
    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", updateScrollParallax, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    updateScrollParallax();

    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", updateScrollParallax);
      window.removeEventListener("resize", onResize);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!overlayFrozen) return;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, [overlayFrozen]);

  return (
    <section
      ref={sectionRef}
      // Fill most of the viewport below the sticky header; leave a band so the
      // testimonials clothesline peeks at scroll-top (desktop). Mobile keeps a
      // tight bottom so quotes sit closer under the art.
      className="relative overflow-x-clip pt-8 pb-2 md:h-[calc(100dvh-12.5rem)] md:flex md:items-center md:pb-0"
    >
      {/*
        Desktop: four side plates. Height fills the 75dvh section; left/right stay
        on their sides of the CTA with a slight center overlap. Parallax by depth.
      */}
      <div className="pointer-events-none absolute inset-0 z-0 hidden md:block" aria-hidden="true">
        <SideArtImage src={BACK_LEFT_SRC} side="left" depth="back" />
        <SideArtImage src={BACK_RIGHT_SRC} side="right" depth="back" />
        <SideArtImage src={FRONT_LEFT_SRC} side="left" depth="front" />
        <SideArtImage src={FRONT_RIGHT_SRC} side="right" depth="front" />
      </div>

      <MobileArt>
        <HeroCta />
      </MobileArt>

      <div className="relative z-10 hidden h-full w-full items-center justify-center md:flex">
        <HeroCta />
      </div>
    </section>
  );
}
